'use client';

import * as Headless from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  widthClassName?: string;
  mobileFullWidth?: boolean;
}

/**
 * Slide-over da destra con focus trap nativo (Headless UI Dialog).
 * Usato per spostare i dettagli/gestione di un elemento fuori dalla vista
 * principale, mantenendo quest'ultima leggibile anche con molti elementi.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  widthClassName = 'max-w-md',
  mobileFullWidth = false,
}: DrawerProps) {
  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50 transition-opacity motion-reduce:transition-none" aria-hidden="true" />
      <div className="fixed inset-0 overflow-hidden">
        <div className={`absolute inset-y-0 right-0 flex max-w-full ${mobileFullWidth ? 'w-full sm:w-auto sm:pl-10' : 'pl-10'}`}>
          <Headless.DialogPanel
            className={`${mobileFullWidth ? 'w-full sm:w-screen' : 'w-screen'} ${widthClassName} bg-white dark:bg-zinc-900 shadow-xl flex flex-col h-full border-l border-zinc-200 dark:border-white/10`}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-zinc-200 dark:border-white/10">
              <Headless.DialogTitle className="text-sm font-semibold text-zinc-900 dark:text-white min-w-0">
                {title}
              </Headless.DialogTitle>
              <button
                type="button"
                onClick={onClose}
                aria-label="Chiudi"
                title="Chiudi"
                className="shrink-0 p-1.5 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
            {footer && (
              <div className="shrink-0 border-t border-zinc-200 px-5 py-4 dark:border-white/10">
                {footer}
              </div>
            )}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}
